import React, { useEffect, useRef, useState } from "react";
import { Stepper } from "@mantine/core";
import { useRouter } from "next/router";

import SubscriptionLength from "/components/BikeOrder/SubscriptionLength";
import CartContext, { CartProvider } from "/context/cart";
import AccessoriesList from "/components/BikeOrder/AccessoriesList";
import Checkout from "/components/BikeOrder/Checkout";
import ChooseAccessoriesIcon from "/components/BikeOrder/Stepper/Icons/ChooseAccessoriesIcon";
import { BikeItemContent } from "../../../pages/sykler/bestill/[bikeId]";
import ChooseSubscriptionIcon from "/components/BikeOrder/Stepper/Icons/ChooseSubscriptionIcon";
import CheckoutIcon from "/components/BikeOrder/Stepper/Icons/CheckoutIcon";

type Props = {
  bikeModel: BikeItemContent;
  customerHasValidCreditCard: boolean;
};

const BikeOrderStepper = ({ bikeModel, customerHasValidCreditCard }: Props) => {
  const [active, setActive] = useState(0);
  const stepperRef = useRef(null);
  const cart = React.useContext(CartContext);
  const router = useRouter();

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    cart.addBike(bikeModel);
    const subscriptionId = router.query.subscriptionId;
    let changeActiveStepTo = 0;

    const subscription =
      bikeModel?.bikeSubscriptions?.find((sub: { id: string }) => sub.id === subscriptionId) ||
      null;

    if (!subscription) {
      setActive(changeActiveStepTo);
      return;
    }

    cart.addSubscription({
      title: subscription.name,
      price: subscription.price,
      id: subscription.id
    });
    changeActiveStepTo = 1;

    setActive(changeActiveStepTo);
  }, []);

  const nextStep: () => void = () => {
    setActive((current) => (current < 4 ? current + 1 : current));
    // @ts-ignore
    stepperRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const prevStep: () => void = () => {
    setActive((current) => (current > 0 ? current - 1 : current));
    // @ts-ignore
    stepperRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <div ref={stepperRef} />
      <Stepper
        sx={{
          "& .mantine-UnstyledButton-root": {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
          },
          "& .mantine-Stepper-step": {
            width: 100
          },

          "& .mantine-Stepper-stepBody": {
            display: "contents"
          },
          "& .mantine-Stepper-separator": {
            marginBottom: 20,
            height: 3,

            marginLeft: -20,
            marginRight: -20,
            background: "#FFD7C0"
          },
          "& .mantine-Stepper-separatorActive": {
            marginBottom: 20,
            background: "#F45338"
          },
          "& .mantine-Stepper-stepWrapper": {},
          "& .mantine-Stepper-stepIcon": {
            backgroundColor: "#FFD7C0"
          },
          "& .mantine-13929wa[data-progress]": {
            borderColor: "#F45338"
          },
          "& .mantine-13929wa[data-completed]": {
            border: "none"
          },
          "& .mantine-Stepper-stepCompletedIcon": {
            backgroundColor: "#F45338",
            borderRadius: "50%"
          },
          "& .mantine-Stepper-stepLabel": {
            paddingTop: 10,
            fontSize: 16
          }
        }}
        active={active}
        onStepClick={setActive}
      >
        <Stepper.Step
          label="Abonnement"
          icon={<ChooseSubscriptionIcon />}
          completedIcon={<ChooseSubscriptionIcon />}
          allowStepSelect={active > 0}
        >
          <SubscriptionLength nextStep={nextStep} prevStep={prevStep} />
        </Stepper.Step>

        <Stepper.Step
          label="Utstyr"
          icon={<ChooseAccessoriesIcon />}
          completedIcon={<ChooseAccessoriesIcon />}
          allowStepSelect={active > 1}
        >
          <AccessoriesList nextStep={nextStep} prevStep={prevStep} />
        </Stepper.Step>

        <Stepper.Step label="Utsjekk" icon={<CheckoutIcon />} allowStepSelect={active > 2}>
          <Checkout prevStep={prevStep} customerHasValidCreditCard={customerHasValidCreditCard} />
        </Stepper.Step>
      </Stepper>
    </>
  );
};

const Wrapper = (props: { bikeModel: BikeItemContent; customerHasValidCreditCard: boolean }) => {
  return (
    <CartProvider>
      <BikeOrderStepper {...props} />
    </CartProvider>
  );
};

export default Wrapper;
